import React from 'react';
import type { FormikProps } from 'formik';
import { Formik } from 'formik';
import { useIntl } from 'react-intl';

import { Box, Grid } from '@mui/material';

import { TranslationKeys } from '../../../../../translations';

import { CivilStatusEnum } from '../../../../../types/civilStatus.types';

import { useGenderSelectItems } from '../../../../../hooks/useGenderSelectItems';

import { InputFieldFactory } from '../../../../../components/FormikField';

type PartnerStepProps = {
  employeeCivilStatus: CivilStatusEnum;
  onSubmit: () => Promise<void>;
  stepState: Record<string, any>;
  loading?: boolean;
};

export const PartnerStep = React.forwardRef(
  ({ employeeCivilStatus, onSubmit, stepState, loading = false }: PartnerStepProps, ref) => {
    const formRef = React.createRef<FormikProps<any>>();
    const { genderSelectItems } = useGenderSelectItems();
    const intl = useIntl();

    React.useImperativeHandle(
      ref,
      () => ({
        submit() {
          formRef?.current?.submitForm();
        },
        getState() {
          return {
            form: formRef?.current?.values,
          };
        },
      }),
      [formRef],
    );

    const isPartnerRequired = employeeCivilStatus === CivilStatusEnum.Married;

    const fields = React.useMemo(
      () => [
        {
          name: 'employee.partner.personName.initials',
          type: 'text',
          required: isPartnerRequired,
          header: TranslationKeys.global_initials,
          placeholder: intl.formatMessage({ id: TranslationKeys.global_initials }),
          loading: loading,
          schema: (schema: any) => schema.trim().strict().max(255),
        },
        {
          name: 'employee.partner.personName.firstName',
          type: 'text',
          required: false,
          header: TranslationKeys.global_firstName,
          placeholder: intl.formatMessage({ id: TranslationKeys.global_firstName }),
          loading: loading,
          schema: (schema: any) => schema.trim().strict().max(255),
        },
        {
          name: 'employee.partner.personName.lastNamePrefix',
          type: 'text',
          required: false,
          header: TranslationKeys.global_lastNamePrefix,
          placeholder: intl.formatMessage({ id: TranslationKeys.global_lastNamePrefix }),
          loading: loading,
          schema: (schema: any) => schema.trim().strict().max(12),
        },
        {
          name: 'employee.partner.personName.lastName',
          type: 'text',
          required: isPartnerRequired,
          header: TranslationKeys.global_lastName,
          placeholder: intl.formatMessage({ id: TranslationKeys.global_lastName }),
          loading: loading,
          schema: (schema: any) => schema.trim().strict().max(255),
        },
        {
          name: 'employee.partner.gender',
          type: 'autocomplete',
          required: isPartnerRequired,
          header: TranslationKeys.global_gender,
          placeholder: intl.formatMessage({ id: TranslationKeys.global_gender }),
          loading: loading,
          items: genderSelectItems,
        },
        {
          name: 'employee.partner.dateOfBirth',
          type: 'date',
          required: isPartnerRequired,
          header: TranslationKeys.employees_detail_dateOfBirth,
          placeholder: intl.formatMessage({ id: TranslationKeys.employees_detail_dateOfBirth }),
          loading: loading,
        },
        {
          name: 'employee.partner.socialSecurityNumber',
          type: 'text',
          required: false,
          header: TranslationKeys.social_security_number,
          placeholder: intl.formatMessage({ id: TranslationKeys.social_security_number }),
          loading: loading,
          schema: (schema: any) => schema.trim().strict().max(12),
        },
        {
          name: 'employee.partner.startOfRelationship',
          type: 'date',
          required: isPartnerRequired,
          header: TranslationKeys.events_content_header_relationStartDate,
          placeholder: intl.formatMessage({ id: TranslationKeys.events_content_header_relationStartDate }),
          loading: loading,
        },
        {
          name: 'employee.partner.endOfRelationship',
          type: 'date',
          required: false,
          header: TranslationKeys.events_content_header_relationEndDate,
          placeholder: intl.formatMessage({ id: TranslationKeys.events_content_header_relationEndDate }),
          loading: loading,
        },
      ],
      [genderSelectItems, intl, isPartnerRequired, loading],
    );

    const initialValues = React.useMemo(() => {
      return {
        employee: {
          partner: {
            personName: {
              firstName: null,
              lastNamePrefix: null,
              lastName: null,
            },
            gender: null,
            dateOfBirth: null,
            socialSecurityNumber: null,
            startOfRelationship: null,
            endOfRelationship: null,
          },
          ...(stepState.form.employee || {}),
        },
      };
    }, [stepState.form]);

    return (
      <Formik enableReinitialize initialValues={initialValues} onSubmit={onSubmit} innerRef={formRef}>
        {() => (
          <>
            <Grid container spacing={2}>
              {fields.map((field, index) => {
                return (
                  <Grid item xs={4} key={index}>
                    <InputFieldFactory field={field} />
                  </Grid>
                );
              })}
            </Grid>
            <Box p={1} />
          </>
        )}
      </Formik>
    );
  },
);

PartnerStep.displayName = 'PartnerStep';
